/**
 * Step 3 — scaling: three calm cards — performing (grow), weak (shrink),
 * hold-back — each one rule and one line of action; the mechanics live in
 * one ⓘ (user, 13/10/2026: minimal, intuitive).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ShieldAlert, TrendingDown, TrendingUp } from 'lucide-react';
import { type DropGenVerdictSource, mixFor } from '@trafficpulse/shared';

import { HintDot } from '@/components/ui';

import type { DraftFlatRule, DropGenPlanDetail } from '../../../../drop-generator.types';
import { draftTextMix } from '../../../shared/texts/text-mix';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { OnlyWhen } from '../card/only-when/OnlyWhen';
import { VerdictCard } from '../card/VerdictCard';
import { HistoryLine } from '../history/HistoryLine';
import { withSuggestion } from '../history/history-words';
import { ReplayPanel } from '../replay/ReplayPanel';
import { RightNowLine } from '../source/RightNowLine';
import { rowsForSource } from '../source/source-words';
import { ScalingAction } from './ScalingAction';
import { ShrinkAction } from './ShrinkAction';

const lead = (word: string, tone: string) => (
  <>
    The list is <span className={tone}>{word}</span>
  </>
);

export const ScalingStep: FC<{
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
}> = ({ editor, plan, containerId }) => {
  const { scaling, defaultAmount, verdict } = editor.draft;
  // Phase 37 — the texts these drops send, mirrored here.
  const proven = (kind: 'SCALED' | 'SHRUNK') => mixFor(draftTextMix(editor.draft), kind);
  const patchRule = (key: 'performing' | 'guard' | 'weak') => (patch: Partial<DraftFlatRule>) => editor.scalingPatch({ [key]: { ...scaling[key], ...patch } });
  const historyId = containerId ?? editor.draft.masterId;
  // Phase 39 — one source for the three rules: today's drops, or the list's own stats over a range.
  const onDrops = scaling.source.kind === 'drops';
  const setSource = (source: DropGenVerdictSource) =>
    editor.scalingPatch({
      source,
      performing: {
        ...scaling.performing,
        rows: rowsForSource(scaling.performing.rows, source.kind),
      },
      weak: {
        ...scaling.weak,
        rows: rowsForSource(scaling.weak.rows, source.kind),
      },
      guard: {
        ...scaling.guard,
        rows: rowsForSource(scaling.guard.rows, source.kind),
      },
    });
  const history = (key: 'performing' | 'weak') =>
    onDrops ? (
      <HistoryLine countryKey={plan.countryKey} id={historyId} kind="container" which={key} onUse={(s) => patchRule(key)({ rows: withSuggestion(scaling[key].rows, s) })} />
    ) : (
      <RightNowLine countryKey={plan.countryKey} containerId={containerId} card="scaling" source={scaling.source} rule={scaling[key]} minSampleSent={verdict.minSampleSent} />
    );
  const hint = `Judged by the same checks as the terrible rule (Evaluate verdicts, or the schedule every 15 minutes), on the numbers the source names: today's drops to the group's list added up (SMSWiser's and the tracker's counters, rates recomputed from the totals) or the list's own tracker stats over the date range. Each check takes one step — but only on new results: a check that reads the same settled sends as the last step takes no step, so one result never scales twice. A terrible list burns and never steps; a weak one shrinks; a performing one grows unless the hold-back holds. Only unsent drafts change — sent ones never do; a grown drop is marked ×factor on the timeline. A new day starts at step 0.`;
  // 20/10/2026 — the list rule's sample and settle time, as "Only when" rows inside the conditions.
  const judgedOn = (
    <OnlyWhen
      sample={{ value: verdict.minSampleSent, onChange: (minSampleSent) => editor.verdictPatch({ minSampleSent }), unit: onDrops ? 'sms' : 'visits', shared: true }}
      settle={onDrops ? { value: verdict.evaluationDelayMin, onChange: (evaluationDelayMin) => editor.verdictPatch({ evaluationDelayMin }), shared: true } : undefined}
    />
  );
  const starter = (metric: string, op: string, value: number) => (onDrops ? { metric, op, value } : { metric: 'cr', op, value: 2 });
  return (
    <div className="flex flex-col gap-3">
      <VerdictCard
        card="scaling"
        source={scaling.source}
        onSource={setSource}
        icon={TrendingUp}
        lead={lead('performing', 'text-success')}
        aside={<HintDot label={hint} />}
        rule={scaling.performing}
        onPatch={patchRule('performing')}
        starter={starter('deliveredRate', '>', 0.9)}
        footer={judgedOn}
        outcome={{ tone: 'success', icon: TrendingUp, label: 'grow' }}
      >
        <ScalingAction scaling={scaling} defaultAmount={defaultAmount} onPatch={editor.scalingPatch} proven={proven('SCALED')} />
        {history('performing')}
      </VerdictCard>
      <VerdictCard
        card="scaling"
        source={scaling.source}
        icon={TrendingDown}
        lead={lead('weak', 'text-warning')}
        rule={scaling.weak}
        onPatch={patchRule('weak')}
        starter={starter('deliveredRate', '<', 0.9)}
        footer={judgedOn}
        outcome={{ tone: 'warning', icon: TrendingDown, label: 'shrink' }}
      >
        <ShrinkAction scaling={scaling} defaultAmount={defaultAmount} onPatch={editor.scalingPatch} proven={proven('SHRUNK')} />
        {history('weak')}
      </VerdictCard>
      <VerdictCard
        card="scaling"
        source={scaling.source}
        icon={ShieldAlert}
        lead={
          <>
            Even when performing, <span className="text-warning">do not grow</span>
          </>
        }
        rule={scaling.guard}
        onPatch={patchRule('guard')}
        starter={starter('botRate', '>', 0.15)}
        footer={judgedOn}
        outcome={{ tone: 'warning', icon: ShieldAlert, label: 'hold' }}
      >
        <p className="text-xs text-muted-foreground">the step is skipped this check — the drops keep their amount</p>
      </VerdictCard>
      <ReplayPanel kind="scaling" draft={editor.draft} plan={plan} containerId={containerId} />
    </div>
  );
};
